import DocumentService from '../../services/ptDocumentService/index';
import Document from '../../services/ptDocumentService/document';
import { getCompanyId } from '../../Tools/utils';

type ProgressUpload = (progress: ProgressEvent) => void;

/**
 * @property destinationBucket - Optional bucket to move the file to. If empty, document is stored in
 * @property transactionId - Optional unique key in which multiple documents can be linked and queried.
 * @property companyId - Optional company id to be stored under.
 * @property name - Optional download name if using main bucket.
 * @property getUrl - If TRUE returns a pre-signed url to the stored document.
 **/
interface UploadOptions {
  destinationBucket?: string;
  transactionId?: string;
  companyId?: string;
  name?: string;
  getUrl?: boolean;
}

export interface PreSignedUrl {
  url: string;
}

export interface UploadMetaData {
  files: Document[];
  transactionId?: string;
  companyId?: string;
  destination?: string;
}

export interface DocumentSaveResponse {
  completedFiles: Document[];
  failedFiles: Document[];
}

export interface DocumentDeleteResponse {
  ok: number;
}

export default class S3DocumentController {
  documentService: DocumentService;

  constructor() {
    this.documentService = new DocumentService();
  }

  /**
   * Uploads a file to stage and triggers the move to permanent storage.
   *
   * @param key - Unique key stored in S3.
   * @param mimeType - MimeType of the file being stored.
   * @param file - File to store.
   * @param progressUpload - Optional function callback to keep track of upload progress.
   * @param options - Options to be used during upload and saving.
   * @return A pre-signed url to the uploaded document if getUrl is true else an empty Promise
   **/
  async uploadAndSave(
    key: string,
    mimeType: string,
    file: any,
    progressUpload?: ProgressUpload,
    options?: UploadOptions,
  ): Promise<string | undefined> {
    const { transactionId, companyId, destinationBucket, getUrl, name } = options || {};
    await this.uploadToStage(key, mimeType, file, progressUpload, options);
    await this.save([file], transactionId, companyId, destinationBucket, progressUpload);
    if (getUrl) {
      const url = await this.getPresignedUrl(
        key,
        name || key,
        'application/octet-stream',
        false,
        false,
        false,
        destinationBucket,
      );
      return url.url;
    }
  }

  /**
   * Uploads a file to stage.
   *
   * @param key - Unique key stored in S3.
   * @param mimeType - MimeType of the file being stored.
   * @param file - File to store.
   * @param progressUpload - Optional function callback to keep track of upload progress.
   * @param options - Options to be used during upload.
   * @return Promise<string | undefined> A pre-signed url to the uploaded document if getUrl is true else an empty Promise
   **/
  async uploadToStage(
    key: string,
    mimeType: string,
    file: any,
    progressUpload?: ProgressUpload,
    options?: UploadOptions,
  ): Promise<string | undefined> {
    const { name, getUrl } = options || {};
    let url = await this.getPresignedUrl(key, name || key, mimeType, true, false);
    await this.documentService.uploadFile(file, url.url, progressUpload ?? (() => {}));
    if (getUrl) {
      url = await this.getPresignedUrl(key, name || key, 'application/octet-stream', false, true);
      return url.url;
    } else {
      return;
    }
  }

  /**
   * Uploads a file to carrierpro documents.
   *
   * @param key - Unique key stored in S3.
   * @param mimeType - MimeType of the file being stored.
   * @param file - File to store.
   * @param progressUpload - Optional function callback to keep track of upload progress.
   * @param options - Options to be used during upload.
   * @return Promise<string | undefined> A pre-signed url to the uploaded document if getUrl is true else an empty Promise
   **/
  async uploadToDocuments(
    key: string,
    mimeType: string,
    file: any,
    progressUpload?: ProgressUpload,
    options?: UploadOptions,
  ): Promise<string | undefined> {
    const { name, getUrl } = options || {};
    let url = await this.getPresignedUrl(key, name || key, mimeType, true, false);
    await this.documentService.uploadFile(file, url.url, progressUpload ?? (() => {}));
    if (getUrl) {
      url = await this.getPresignedUrl(key, name || key, 'application/octet-stream', false, false);
      return url.url;
    } else {
      return;
    }
  }

  /**
   * Downloads a file through the browser.
   *
   * @param key - Unique key stored in S3.
   * @param name - Name that the file we be downloaded as.
   * @param isStaged - True: Download from stage bucket. False: Download from carrierpro-documents or
   *                   bucket if provided.
   * @param bucket - Optional name of bucket to download from.
   * @return Promise<undefined>
   **/
  async downloadDocument(
    key: string,
    name: string,
    isStaged: boolean,
    bucket?: string,
    isSkipStagingBucket?: boolean,
  ): Promise<void> {
    const url: PreSignedUrl = await this.getPresignedUrl(
      key,
      name,
      'application/octet-stream',
      false,
      isStaged,
      true,
      bucket,
      undefined,
      undefined,
      isSkipStagingBucket,
    );

    window.open(url.url, '_blank');
  }

  /**
   * Gets a signed url to access document.
   *
   * @param key - Unique key stored in S3.
   * @param name - Name that the file we be downloaded as.
   * @param mimeType - MimeType of the file being stored.
   * @param isUpload - True: Signs as GET request. False: Signs as POST request.
   * @param isStaged - True: Target stage bucket. False: Target carrierpro-documents or bucket if provided.
   * @param isView - True: Opens file in new tab. False: Downloads file.
   * @param bucket - Optional name of bucket to download from.
   * @return Promise<PreSignedUrl> A pre-signed url to the uploaded document.
   **/
  async getPresignedUrl(
    key: string,
    name: string,
    mimeType: string,
    isUpload: boolean,
    isStaged: boolean,
    isView?: boolean,
    bucket?: string,
    region?: string,
    getUrl?: boolean,
    isSkipStagingBucket?: boolean,
  ): Promise<PreSignedUrl> {
    return this.documentService.getPresignedUrl(
      key,
      name,
      mimeType,
      isUpload,
      isStaged,
      isView,
      bucket,
      region,
      getUrl,
      isSkipStagingBucket,
    );
  }

  /**
   * Delete documents from S3
   * @param files - Files data to delete.
   * @param transactionId - Transaction id to group files.
   * @return Promise<undefined>
   **/
  async delete(files: Document[], transactionId?: string): Promise<DocumentDeleteResponse> {
    const metadata = this.mapFilesToDocumentRequest(files, transactionId);
    if (metadata.files.some((file) => !file._id)) {
      const companyId = await getCompanyId();
      const docsWithIds = await this.documentService.getDocuments(transactionId || '', companyId || '');
      metadata.files.forEach((file) => {
        file._id = docsWithIds.find((doc: { key: string }) => doc.key === file.key)._id;
      });
    }
    return await this.documentService.deleteDocuments(metadata);
  }

  /**
   * Initiate move from stage to permanent storage in S3
   * @param files - File data to save.
   * @param transactionId - Id to group files if using main bucket.
   * @param companyId - Company to save files under in DB if using main bucket.
   * @param destinationBucket - Bucket to save to. If undefined saves in carrierpro-documents
   *                            and stores in db://carrierpro/files
   * @param progressUpload - Optional function callback to keep track of save progress.
   * @return Promise<DocumentSaveResponse>
   **/
  async save(
    files: Document[],
    transactionId?: string,
    companyId?: string,
    destinationBucket?: string,
    progressUpload?: ProgressUpload,
  ): Promise<DocumentSaveResponse> {
    const metadata = this.mapFilesToDocumentRequest(files, transactionId, companyId, destinationBucket);
    return await this.documentService.saveFileMetaData(metadata, progressUpload);
  }

  /**
   * Gets file data stored in main bucket.
   * @param documentKey - Id of grouped files.
   * @param companyId - Company id that files are saved under.
   * @return Promise<Document[]> Document data.
   **/
  async getDocuments(documentKey: string, companyId: string): Promise<Document[]> {
    return await this.documentService.getDocuments(documentKey, companyId);
  }

  private mapFilesToDocumentRequest(
    files: Document[],
    transactionId?: string,
    companyId?: string,
    destinationBucket?: string,
  ): UploadMetaData {
    const fileData = files.map((file: Document): any => {
      return {
        _id: file._id || null,
        name: file.name,
        key: file.key,
        metadata: file.metadata,
      };
    });
    return {
      transactionId: transactionId,
      files: fileData,
      companyId: companyId,
      destination: destinationBucket || undefined,
    };
  }
}
